import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  GlobeIcon,
  CoinsIcon,
  PhoneIcon,
  Building2Icon,
  SearchIcon,
  Loader2Icon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  FileTextIcon,
  TagIcon,
  LayersIcon,
  UsersIcon,
} from 'lucide-react';
import CurrencyLibrary from '../CurrencyLibrary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UltraLibrary = () => {
  return (
    <div className="space-y-6" data-testid="ultra-library-page">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-md">
            <Building2Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Kütüphane Yönetimi</h1>
            <p className="text-sm text-gray-500">
              Platform genelinde kullanılan referans verileri yönetin
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="countries" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 w-full h-auto bg-gray-100 p-1 rounded-lg">
          <TabsTrigger
            value="countries"
            className="flex items-center space-x-2 py-2.5"
            data-testid="library-tab-countries"
          >
            <GlobeIcon className="w-4 h-4" />
            <span>Ülke &amp; Şehir</span>
          </TabsTrigger>
          <TabsTrigger
            value="currencies"
            className="flex items-center space-x-2 py-2.5"
            data-testid="library-tab-currencies"
          >
            <CoinsIcon className="w-4 h-4" />
            <span>Para Birimleri</span>
          </TabsTrigger>
          <TabsTrigger
            value="phone-codes"
            className="flex items-center space-x-2 py-2.5"
            data-testid="library-tab-phone-codes"
          >
            <PhoneIcon className="w-4 h-4" />
            <span>Telefon Kodları</span>
          </TabsTrigger>
          <TabsTrigger
            value="expense-types"
            className="flex items-center space-x-2 py-2.5"
            data-testid="library-tab-expense-types"
          >
            <FileTextIcon className="w-4 h-4" />
            <span>Genel Gider Türleri</span>
          </TabsTrigger>
          <TabsTrigger
            value="categories"
            className="flex items-center space-x-2 py-2.5"
            data-testid="library-tab-categories"
          >
            <TagIcon className="w-4 h-4" />
            <span>Kategoriler</span>
          </TabsTrigger>
          <TabsTrigger
            value="expense-categories"
            className="flex items-center space-x-2 py-2.5"
            data-testid="library-tab-expense-categories"
          >
            <LayersIcon className="w-4 h-4" />
            <span>Harcama Kategorileri</span>
          </TabsTrigger>
          <TabsTrigger
            value="exhibition"
            className="flex items-center space-x-2 py-2.5"
            data-testid="library-tab-exhibition"
          >
            <Building2Icon className="w-4 h-4" />
            <span>Fuar Merkezleri</span>
          </TabsTrigger>
          <TabsTrigger
            value="departments"
            className="flex items-center space-x-2 py-2.5"
            data-testid="library-tab-departments"
          >
            <UsersIcon className="w-4 h-4" />
            <span>Departmanlar</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="countries" className="mt-6">
          <CountriesCitiesPanel />
        </TabsContent>

        <TabsContent value="currencies" className="mt-6">
          <CurrencyLibrary />
        </TabsContent>

        <TabsContent value="phone-codes" className="mt-6">
          <PhoneCodesPanel />
        </TabsContent>

        <TabsContent value="expense-types" className="mt-6">
          <GlobalExpenseTypesPanel />
        </TabsContent>

        <TabsContent value="categories" className="mt-6">
          <GlobalCategoriesPanel />
        </TabsContent>

        <TabsContent value="expense-categories" className="mt-6">
          <GlobalExpenseCategoriesPanel />
        </TabsContent>

        <TabsContent value="exhibition" className="mt-6">
          <ExhibitionCentersPanel />
        </TabsContent>

        <TabsContent value="departments" className="mt-6">
          <GlobalDepartmentsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
};

/* -------------------- Countries & Cities Panel (Ultra Admin CRUD) -------------------- */
const CountriesCitiesPanel = () => {
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingCities, setLoadingCities] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [countryForm, setCountryForm] = useState({ code: '', name: '', name_en: '' });
  const [showCityModal, setShowCityModal] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [cityForm, setCityForm] = useState({ name: '', name_en: '', plate_code: '' });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('ultra_admin_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/ultra-admin/countries`, {
        headers: authHeader(),
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setCountries(list);
      if (!selectedCountry && list.length) {
        const tr = list.find((c) => c.code === 'TR' || c.code === 'TUR') || list[0];
        setSelectedCountry(tr);
        fetchCities(tr.code);
      } else if (selectedCountry) {
        // refresh currently-selected
        const refreshed = list.find((c) => c.code === selectedCountry.code);
        if (refreshed) setSelectedCountry(refreshed);
      }
    } catch (err) {
      toast.error('Ülkeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async (countryCode) => {
    try {
      setLoadingCities(true);
      const res = await axios.get(
        `${API}/ultra-admin/cities?country=${encodeURIComponent(countryCode)}`,
        { headers: authHeader() },
      );
      setCities(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  const openAddCountry = () => {
    setEditingCountry(null);
    setCountryForm({ code: '', name: '', name_en: '' });
    setShowCountryModal(true);
  };

  const openEditCountry = (c) => {
    setEditingCountry(c);
    setCountryForm({ code: c.code, name: c.name || '', name_en: c.name_en || '' });
    setShowCountryModal(true);
  };

  const saveCountry = async () => {
    const code = (countryForm.code || '').trim().toUpperCase();
    const name = (countryForm.name || '').trim();
    if (!code || !name) {
      toast.error('Ülke kodu ve adı zorunludur');
      return;
    }
    try {
      setSaving(true);
      if (editingCountry) {
        await axios.put(
          `${API}/ultra-admin/countries/${editingCountry.code}`,
          { name, name_en: countryForm.name_en?.trim() || null, is_active: true },
          { headers: authHeader() },
        );
        toast.success('Ülke güncellendi');
      } else {
        await axios.post(
          `${API}/ultra-admin/countries`,
          { code, name, name_en: countryForm.name_en?.trim() || null },
          { headers: authHeader() },
        );
        toast.success('Ülke eklendi');
      }
      setShowCountryModal(false);
      fetchCountries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const deleteCountry = async (c) => {
    if (!window.confirm(`"${c.name}" ve tüm şehirleri silinsin mi? Geri alınamaz.`)) return;
    try {
      await axios.delete(`${API}/ultra-admin/countries/${c.code}`, {
        headers: authHeader(),
      });
      toast.success('Ülke silindi');
      if (selectedCountry?.code === c.code) {
        setSelectedCountry(null);
        setCities([]);
      }
      fetchCountries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const openAddCity = () => {
    if (!selectedCountry) {
      toast.error('Önce bir ülke seçin');
      return;
    }
    setEditingCity(null);
    setCityForm({ name: '', name_en: '', plate_code: '' });
    setShowCityModal(true);
  };

  const openEditCity = (c) => {
    setEditingCity(c);
    setCityForm({
      name: c.name || '',
      name_en: c.name_en || '',
      plate_code: c.plate_code || '',
    });
    setShowCityModal(true);
  };

  const saveCity = async () => {
    const name = (cityForm.name || '').trim();
    if (!name) {
      toast.error('Şehir adı zorunludur');
      return;
    }
    try {
      setSaving(true);
      if (editingCity) {
        await axios.put(
          `${API}/ultra-admin/cities/${editingCity.id}`,
          {
            name,
            name_en: cityForm.name_en?.trim() || null,
            plate_code: cityForm.plate_code?.trim() || null,
          },
          { headers: authHeader() },
        );
        toast.success('Şehir güncellendi');
      } else {
        await axios.post(
          `${API}/ultra-admin/cities`,
          {
            country_code: selectedCountry.code,
            name,
            name_en: cityForm.name_en?.trim() || null,
            plate_code: cityForm.plate_code?.trim() || null,
          },
          { headers: authHeader() },
        );
        toast.success('Şehir eklendi');
      }
      setShowCityModal(false);
      fetchCities(selectedCountry.code);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const deleteCity = async (city) => {
    if (!window.confirm(`"${city.name}" silinsin mi?`)) return;
    try {
      await axios.delete(`${API}/ultra-admin/cities/${city.id}`, {
        headers: authHeader(),
      });
      toast.success('Şehir silindi');
      fetchCities(selectedCountry.code);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const seedTRCities = async () => {
    if (!window.confirm('81 Türkiye ili eklensin mi? Halihazırda olanlar atlanır.')) return;
    try {
      setSeeding(true);
      const res = await axios.post(
        `${API}/ultra-admin/cities/seed-tr`,
        {},
        { headers: authHeader() },
      );
      toast.success(res.data?.message || 'Şehirler eklendi');
      if (selectedCountry?.code === 'TR') fetchCities('TR');
    } catch (err) {
      toast.error('İçe aktarma başarısız');
    } finally {
      setSeeding(false);
    }
  };

  const filteredCountries = countries.filter((c) =>
    c.name?.toLowerCase().includes(countrySearch.toLowerCase()),
  );
  const filteredCities = cities.filter((c) =>
    c.name?.toLowerCase().includes(citySearch.toLowerCase()),
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Countries */}
      <Card className="p-5">
        <div className="flex items-start justify-between mb-4 gap-2 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <GlobeIcon className="w-5 h-5 mr-2 text-indigo-600" />
              Ülkeler
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Toplam {countries.length} ülke
            </p>
          </div>
          <Button
            onClick={openAddCountry}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            size="sm"
            data-testid="add-country-btn"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Yeni Ülke
          </Button>
        </div>
        <div className="relative mb-3">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
            placeholder="Ülke ara..."
            className="pl-10"
            data-testid="library-country-search"
          />
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2Icon className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : filteredCountries.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              Sonuç bulunamadı
            </div>
          ) : (
            <div className="max-h-[480px] overflow-y-auto divide-y divide-gray-100">
              {filteredCountries.map((country) => {
                const active = selectedCountry?.code === country.code;
                return (
                  <div
                    key={country.code}
                    data-testid={`library-country-row-${country.code}`}
                    className={`group flex items-center justify-between px-4 py-3 transition-colors ${
                      active ? 'bg-indigo-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCountry(country);
                        fetchCities(country.code);
                      }}
                      className="flex-1 text-left flex items-center justify-between"
                    >
                      <span
                        className={`font-medium ${
                          active ? 'text-indigo-700' : 'text-gray-700'
                        }`}
                      >
                        {country.name}
                      </span>
                      <span className="text-xs text-gray-500 font-mono mr-2">
                        {country.code}
                      </span>
                    </button>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => openEditCountry(country)}
                        className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-100 rounded"
                        data-testid={`edit-country-${country.code}`}
                        aria-label="Düzenle"
                      >
                        <PencilIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteCountry(country)}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                        data-testid={`delete-country-${country.code}`}
                        aria-label="Sil"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {/* Cities */}
      <Card className="p-5">
        <div className="flex items-start justify-between mb-4 gap-2 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Building2Icon className="w-5 h-5 mr-2 text-indigo-600" />
              Şehirler
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {selectedCountry
                ? `${selectedCountry.name} — ${cities.length} şehir`
                : 'Önce bir ülke seçin'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {(selectedCountry?.code === 'TR' || selectedCountry?.code === 'TUR') && cities.length < 81 && (
              <Button
                onClick={seedTRCities}
                variant="outline"
                size="sm"
                disabled={seeding}
                data-testid="seed-tr-cities-btn"
              >
                {seeding ? 'Ekleniyor...' : '81 İli İçe Aktar'}
              </Button>
            )}
            <Button
              onClick={openAddCity}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              size="sm"
              disabled={!selectedCountry}
              data-testid="add-city-btn"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Yeni Şehir
            </Button>
          </div>
        </div>
        <div className="relative mb-3">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={citySearch}
            onChange={(e) => setCitySearch(e.target.value)}
            placeholder="Şehir ara..."
            className="pl-10"
            disabled={!selectedCountry}
            data-testid="library-city-search"
          />
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {loadingCities ? (
            <div className="flex items-center justify-center py-8">
              <Loader2Icon className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : !selectedCountry ? (
            <div className="py-12 text-center text-sm text-gray-500">
              Sol taraftan bir ülke seçin
            </div>
          ) : filteredCities.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">
              Bu ülke için kayıtlı şehir yok. {(selectedCountry.code === 'TR' || selectedCountry.code === 'TUR') && '"81 İli İçe Aktar" ile başlatabilirsiniz.'}
            </div>
          ) : (
            <div className="max-h-[480px] overflow-y-auto divide-y divide-gray-100">
              {filteredCities.map((city) => (
                <div
                  key={city.id}
                  data-testid={`library-city-row-${city.id}`}
                  className="group flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-50"
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{city.name}</span>
                    {city.name_en && (
                      <span className="text-xs text-gray-500">EN · {city.name_en}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {city.plate_code && (
                      <span className="text-xs text-gray-500 font-mono mr-2">
                        {city.plate_code}
                      </span>
                    )}
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEditCity(city)}
                        className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-100 rounded"
                        data-testid={`edit-city-${city.id}`}
                        aria-label="Düzenle"
                      >
                        <PencilIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteCity(city)}
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                        data-testid={`delete-city-${city.id}`}
                        aria-label="Sil"
                      >
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Country Modal */}
      <Dialog open={showCountryModal} onOpenChange={setShowCountryModal}>
        <DialogContent className="max-w-md" data-testid="country-modal">
          <DialogHeader>
            <DialogTitle>
              {editingCountry ? 'Ülkeyi Düzenle' : 'Yeni Ülke'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="country-code">Ülke Kodu (ISO)</Label>
              <Input
                id="country-code"
                value={countryForm.code}
                onChange={(e) =>
                  setCountryForm({ ...countryForm, code: e.target.value.toUpperCase() })
                }
                placeholder="Örn: TR, US, DE"
                maxLength={4}
                disabled={!!editingCountry}
                data-testid="country-code-input"
              />
              {editingCountry && (
                <p className="text-xs text-gray-500 mt-1">
                  Mevcut ülkenin kodu değiştirilemez.
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="country-name">Ülke Adı (Türkçe)</Label>
              <Input
                id="country-name"
                value={countryForm.name}
                onChange={(e) =>
                  setCountryForm({ ...countryForm, name: e.target.value })
                }
                placeholder="Örn: Türkiye"
                data-testid="country-name-input"
              />
            </div>
            <div>
              <Label htmlFor="country-name-en">
                Country Name (English)
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="country-name-en"
                value={countryForm.name_en}
                onChange={(e) =>
                  setCountryForm({ ...countryForm, name_en: e.target.value })
                }
                placeholder="e.g. Turkey"
                data-testid="country-name-en-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setShowCountryModal(false)}
              disabled={saving}
            >
              İptal
            </Button>
            <Button
              onClick={saveCountry}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="country-save-btn"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* City Modal */}
      <Dialog open={showCityModal} onOpenChange={setShowCityModal}>
        <DialogContent className="max-w-md" data-testid="city-modal">
          <DialogHeader>
            <DialogTitle>
              {editingCity ? 'Şehri Düzenle' : `${selectedCountry?.name || 'Şehir'} — Yeni Şehir`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="city-name">Şehir Adı (Türkçe)</Label>
              <Input
                id="city-name"
                value={cityForm.name}
                onChange={(e) => setCityForm({ ...cityForm, name: e.target.value })}
                placeholder="Örn: İstanbul"
                data-testid="city-name-input"
              />
            </div>
            <div>
              <Label htmlFor="city-name-en">
                City Name (English)
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="city-name-en"
                value={cityForm.name_en}
                onChange={(e) => setCityForm({ ...cityForm, name_en: e.target.value })}
                placeholder="e.g. Istanbul"
                data-testid="city-name-en-input"
              />
            </div>
            <div>
              <Label htmlFor="city-plate">
                Plaka / Kod
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="city-plate"
                value={cityForm.plate_code}
                onChange={(e) =>
                  setCityForm({ ...cityForm, plate_code: e.target.value })
                }
                placeholder="Örn: 34"
                maxLength={5}
                data-testid="city-plate-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setShowCityModal(false)}
              disabled={saving}
            >
              İptal
            </Button>
            <Button
              onClick={saveCity}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="city-save-btn"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* -------------------- Phone Codes Panel -------------------- */
const PhoneCodesPanel = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCodes();
  }, []);

  const fetchCodes = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/country-phone-codes`);
      setCodes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Telefon kodları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const filtered = codes.filter(
    (c) =>
      c.country_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.code?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <PhoneIcon className="w-5 h-5 mr-2 text-indigo-600" />
            Telefon Kodları
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Toplam {codes.length} ülke kodu
          </p>
        </div>
      </div>
      <div className="relative mb-4">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Ülke veya kod ara (örn. +90, Turkey)..."
          className="pl-10"
          data-testid="library-phone-search"
        />
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[600px] overflow-y-auto pr-1">
          {filtered.map((c, idx) => (
            <div
              key={`${c.code}-${idx}`}
              data-testid={`library-phone-row-${c.code}`}
              className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl leading-none">{c.flag}</span>
                <span className="text-sm font-medium text-gray-800">
                  {c.country_name}
                </span>
              </div>
              <span className="text-sm font-mono text-indigo-600 font-semibold">
                {c.code}
              </span>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full py-12 text-center text-sm text-gray-500">
              Sonuç bulunamadı
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

/* -------------------- Global Expense Types Panel -------------------- */
const GlobalExpenseTypesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [saving, setSaving] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('ultra_admin_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/ultra-admin/global-expense-types`, {
        headers: authHeader(),
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Genel gider türleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openAdd = () => {
    setEditingItem(null);
    setName('');
    setNameEn('');
    setShowAddModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setName(item.expense_type_name);
    setNameEn(item.expense_type_name_en || '');
    setShowAddModal(true);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    const trimmedEn = nameEn.trim();
    if (!trimmed) {
      toast.error('Türkçe ad boş olamaz');
      return;
    }
    try {
      setSaving(true);
      const body = {
        expense_type_name: trimmed,
        expense_type_name_en: trimmedEn || null,
      };
      if (editingItem) {
        await axios.put(
          `${API}/ultra-admin/global-expense-types/${editingItem.id}`,
          body,
          { headers: authHeader() },
        );
        toast.success('Genel gider türü güncellendi');
      } else {
        await axios.post(
          `${API}/ultra-admin/global-expense-types`,
          body,
          { headers: authHeader() },
        );
        toast.success('Genel gider türü eklendi');
      }
      setShowAddModal(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.expense_type_name}" silinsin mi? Daha önce şirketlere senkronlanmış kopyalar etkilenmez.`)) {
      return;
    }
    try {
      await axios.delete(`${API}/ultra-admin/global-expense-types/${item.id}`, {
        headers: authHeader(),
      });
      toast.success('Genel gider türü silindi');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const filtered = items.filter(
    (i) =>
      i.expense_type_name?.toLowerCase().includes(search.toLowerCase()) ||
      i.expense_type_name_en?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Card className="p-5" data-testid="library-expense-types-panel">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FileTextIcon className="w-5 h-5 mr-2 text-indigo-600" />
            Global Genel Gider Türleri
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
            Buradan eklediğiniz her gider türü, müşterilerin sonraki açılışında
            otomatik olarak hesaplarına eklenir. Bir müşteri kendi tarafında
            sildiğinde geri eklenmez. Müşterilerin kendi ekledikleri buraya
            yansımaz.
          </p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="add-global-expense-type-btn"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Yeni Gider Türü
        </Button>
      </div>

      <div className="relative mb-4">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Gider türü ara..."
          className="pl-10"
          data-testid="library-expense-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
          {items.length === 0
            ? 'Henüz global gider türü eklenmemiş'
            : 'Sonuç bulunamadı'}
        </div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {filtered.map((item) => (
            <div
              key={item.id}
              data-testid={`global-expense-row-${item.id}`}
              className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
            >
              <div className="flex flex-col">
                <span className="font-medium text-gray-800">
                  {item.expense_type_name}
                </span>
                {item.expense_type_name_en && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    EN · {item.expense_type_name_en}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(item)}
                  className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                  data-testid={`edit-global-expense-${item.id}`}
                  aria-label="Düzenle"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(item)}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  data-testid={`delete-global-expense-${item.id}`}
                  aria-label="Sil"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md" data-testid="global-expense-modal">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Gider Türünü Düzenle' : 'Yeni Genel Gider Türü'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="global-expense-name">Gider Türü Adı (Türkçe)</Label>
              <Input
                id="global-expense-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Örn: Seyahat Giderleri"
                autoFocus
                data-testid="global-expense-name-input"
              />
            </div>
            <div>
              <Label htmlFor="global-expense-name-en">
                Expense Type Name (English)
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="global-expense-name-en"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                placeholder="e.g. Travel Expenses"
                data-testid="global-expense-name-en-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Kullanıcının tarayıcı dili İngilizce ise bu ad gösterilir,
                yoksa Türkçe ad gösterilir.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              disabled={saving}
            >
              İptal
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="global-expense-save-btn"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/* -------------------- Global Advance Categories Panel -------------------- */
const GlobalCategoriesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [saving, setSaving] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('ultra_admin_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/ultra-admin/global-categories`, {
        headers: authHeader(),
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Kategoriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openAdd = () => {
    setEditingItem(null);
    setName('');
    setNameEn('');
    setShowAddModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setName(item.name);
    setNameEn(item.name_en || '');
    setShowAddModal(true);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    const trimmedEn = nameEn.trim();
    if (!trimmed) {
      toast.error('Türkçe ad boş olamaz');
      return;
    }
    try {
      setSaving(true);
      const body = { name: trimmed, name_en: trimmedEn || null };
      if (editingItem) {
        await axios.put(
          `${API}/ultra-admin/global-categories/${editingItem.id}`,
          body,
          { headers: authHeader() },
        );
        toast.success('Kategori güncellendi');
      } else {
        await axios.post(`${API}/ultra-admin/global-categories`, body, {
          headers: authHeader(),
        });
        toast.success('Kategori eklendi');
      }
      setShowAddModal(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (
      !window.confirm(
        `"${item.name}" silinsin mi? Daha önce şirketlere senkronlanmış kopyalar etkilenmez.`,
      )
    ) {
      return;
    }
    try {
      await axios.delete(`${API}/ultra-admin/global-categories/${item.id}`, {
        headers: authHeader(),
      });
      toast.success('Kategori silindi');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const filtered = items.filter(
    (i) =>
      i.name?.toLowerCase().includes(search.toLowerCase()) ||
      i.name_en?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Card className="p-5" data-testid="library-categories-panel">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <TagIcon className="w-5 h-5 mr-2 text-indigo-600" />
            Global Avans Kategorileri
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
            Buradan eklediğiniz her kategori, müşterilerin sonraki açılışında
            otomatik olarak hesaplarına eklenir. Bir müşteri kendi tarafında
            sildiğinde geri eklenmez. Müşterilerin kendi ekledikleri buraya
            yansımaz.
          </p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="add-global-category-btn"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Yeni Kategori
        </Button>
      </div>

      <div className="relative mb-4">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kategori ara..."
          className="pl-10"
          data-testid="library-category-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
          {items.length === 0
            ? 'Henüz global kategori eklenmemiş'
            : 'Sonuç bulunamadı'}
        </div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {filtered.map((item) => (
            <div
              key={item.id}
              data-testid={`global-category-row-${item.id}`}
              className="flex items-center justify-between px-4 py-3 hover:bg-gray-50"
            >
              <div className="flex flex-col">
                <span className="font-medium text-gray-800">{item.name}</span>
                {item.name_en && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    EN · {item.name_en}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(item)}
                  className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                  data-testid={`edit-global-category-${item.id}`}
                  aria-label="Düzenle"
                >
                  <PencilIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(item)}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                  data-testid={`delete-global-category-${item.id}`}
                  aria-label="Sil"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md" data-testid="global-category-modal">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Kategoriyi Düzenle' : 'Yeni Kategori'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="global-category-name">Kategori Adı (Türkçe)</Label>
              <Input
                id="global-category-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Örn: Yemek"
                autoFocus
                data-testid="global-category-name-input"
              />
            </div>
            <div>
              <Label htmlFor="global-category-name-en">
                Category Name (English)
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="global-category-name-en"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                placeholder="e.g. Food"
                data-testid="global-category-name-en-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Kullanıcının seçtiği dil İngilizce ise bu ad gösterilir, yoksa
                Türkçe ad gösterilir.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
              disabled={saving}
            >
              İptal
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="global-category-save-btn"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/* -------------------- Global Expense Categories (Harcama Kategorileri) Panel -------------------- */
const GlobalExpenseCategoriesPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [name, setName] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [subs, setSubs] = useState([]); // [{name, name_en}]
  const [saving, setSaving] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('ultra_admin_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/ultra-admin/global-expense-categories`, {
        headers: authHeader(),
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Harcama kategorileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openAdd = () => {
    setEditingItem(null);
    setName('');
    setNameEn('');
    setSubs([]);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setName(item.name);
    setNameEn(item.name_en || '');
    setSubs(
      (item.subcategories || []).map((s) => ({
        name: s.name || '',
        name_en: s.name_en || '',
      })),
    );
    setShowModal(true);
  };

  const addSubRow = () => setSubs([...subs, { name: '', name_en: '' }]);
  const removeSubRow = (idx) => setSubs(subs.filter((_, i) => i !== idx));
  const updateSub = (idx, key, value) => {
    const copy = [...subs];
    copy[idx] = { ...copy[idx], [key]: value };
    setSubs(copy);
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Türkçe ad boş olamaz');
      return;
    }
    try {
      setSaving(true);
      const body = {
        name: trimmed,
        name_en: nameEn.trim() || null,
        subcategories: subs
          .map((s) => ({
            name: (s.name || '').trim(),
            name_en: (s.name_en || '').trim() || null,
          }))
          .filter((s) => s.name),
      };
      if (editingItem) {
        await axios.put(
          `${API}/ultra-admin/global-expense-categories/${editingItem.id}`,
          body,
          { headers: authHeader() },
        );
        toast.success('Harcama kategorisi güncellendi');
      } else {
        await axios.post(
          `${API}/ultra-admin/global-expense-categories`,
          body,
          { headers: authHeader() },
        );
        toast.success('Harcama kategorisi eklendi');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (
      !window.confirm(
        `"${item.name}" silinsin mi? Daha önce şirketlere senkronlanmış kopyalar etkilenmez.`,
      )
    ) {
      return;
    }
    try {
      await axios.delete(
        `${API}/ultra-admin/global-expense-categories/${item.id}`,
        { headers: authHeader() },
      );
      toast.success('Harcama kategorisi silindi');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const filtered = items.filter(
    (i) =>
      i.name?.toLowerCase().includes(search.toLowerCase()) ||
      i.name_en?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Card className="p-5" data-testid="library-expense-categories-panel">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <LayersIcon className="w-5 h-5 mr-2 text-indigo-600" />
            Global Harcama Kategorileri
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
            Avans Kapama ekranında kullanılan kategori &amp; alt kategori
            kataloğu. Müşterilerin sonraki açılışında otomatik senkronlanır.
            Müşteri silerse geri eklenmez, müşterinin kendi eklediği buraya
            yansımaz.
          </p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
          data-testid="add-global-expense-category-btn"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Yeni Kategori
        </Button>
      </div>

      <div className="relative mb-4">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Kategori ara..."
          className="pl-10"
          data-testid="library-expense-category-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
          {items.length === 0 ? 'Henüz harcama kategorisi yok' : 'Sonuç yok'}
        </div>
      ) : (
        <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
          {filtered.map((item) => (
            <div
              key={item.id}
              data-testid={`global-expense-category-row-${item.id}`}
              className="px-4 py-3 hover:bg-gray-50"
            >
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="font-medium text-gray-800">{item.name}</span>
                  {item.name_en && (
                    <span className="text-xs text-gray-500 mt-0.5">
                      EN · {item.name_en}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-md"
                    data-testid={`edit-global-expense-category-${item.id}`}
                    aria-label="Düzenle"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md"
                    data-testid={`delete-global-expense-category-${item.id}`}
                    aria-label="Sil"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {item.subcategories?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {item.subcategories.map((s, i) => (
                    <span
                      key={s.id || i}
                      className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 text-xs border border-indigo-100"
                    >
                      {s.name}
                      {s.name_en && (
                        <span className="ml-1 text-indigo-400">/ {s.name_en}</span>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent
          className="max-w-lg max-h-[85vh] overflow-y-auto"
          data-testid="global-expense-category-modal"
        >
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Harcama Kategorisini Düzenle' : 'Yeni Harcama Kategorisi'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label htmlFor="gec-name">Kategori Adı (Türkçe)</Label>
              <Input
                id="gec-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Örn: Ulaşım"
                autoFocus
                data-testid="gec-name-input"
              />
            </div>
            <div>
              <Label htmlFor="gec-name-en">
                Category Name (English)
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="gec-name-en"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                placeholder="e.g. Transportation"
                data-testid="gec-name-en-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Alt Kategoriler (Subcategories)</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addSubRow}
                  data-testid="gec-add-sub-btn"
                >
                  <PlusIcon className="w-3 h-3 mr-1" /> Alt Kategori
                </Button>
              </div>
              {subs.length === 0 ? (
                <div className="text-xs text-gray-500 py-3 text-center border-2 border-dashed border-gray-200 rounded-lg">
                  Henüz alt kategori eklenmedi
                </div>
              ) : (
                <div className="space-y-2">
                  {subs.map((s, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                      <Input
                        className="col-span-5"
                        placeholder="Türkçe"
                        value={s.name}
                        onChange={(e) => updateSub(idx, 'name', e.target.value)}
                        data-testid={`gec-sub-name-${idx}`}
                      />
                      <Input
                        className="col-span-5"
                        placeholder="English (optional)"
                        value={s.name_en}
                        onChange={(e) =>
                          updateSub(idx, 'name_en', e.target.value)
                        }
                        data-testid={`gec-sub-name-en-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeSubRow(idx)}
                        className="col-span-2 p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md flex items-center justify-center"
                        aria-label="Sil"
                        data-testid={`gec-sub-remove-${idx}`}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={saving}
            >
              İptal
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="gec-save-btn"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/* -------------------- Exhibition Centers Panel (CRUD) -------------------- */
const ExhibitionCentersPanel = () => {
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({
    name: '',
    name_en: '',
    city: '',
    country_code: '',
    address: '',
    website: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('ultra_admin_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/ultra-admin/exhibition-centers`, {
        headers: authHeader(),
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Fuar merkezleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API}/countries`);
      setCountries(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      // optional dropdown - ignore
    }
  };

  useEffect(() => {
    fetchItems();
    fetchCountries();
  }, []);

  const openAdd = () => {
    setEditingItem(null);
    setForm({
      name: '',
      name_en: '',
      city: '',
      country_code: '',
      address: '',
      website: '',
      notes: '',
    });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      name_en: item.name_en || '',
      city: item.city || '',
      country_code: item.country_code || '',
      address: item.address || '',
      website: item.website || '',
      notes: item.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const trimmed = (form.name || '').trim();
    if (!trimmed) {
      toast.error('Fuar merkezi adı boş olamaz');
      return;
    }
    try {
      setSaving(true);
      const body = {
        name: trimmed,
        name_en: form.name_en?.trim() || null,
        city: form.city?.trim() || null,
        country_code: form.country_code?.trim() || null,
        address: form.address?.trim() || null,
        website: form.website?.trim() || null,
        notes: form.notes?.trim() || null,
      };
      if (editingItem) {
        await axios.put(
          `${API}/ultra-admin/exhibition-centers/${editingItem.id}`,
          body,
          { headers: authHeader() },
        );
        toast.success('Fuar merkezi güncellendi');
      } else {
        await axios.post(`${API}/ultra-admin/exhibition-centers`, body, {
          headers: authHeader(),
        });
        toast.success('Fuar merkezi eklendi');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.name}" silinsin mi?`)) return;
    try {
      await axios.delete(`${API}/ultra-admin/exhibition-centers/${item.id}`, {
        headers: authHeader(),
      });
      toast.success('Fuar merkezi silindi');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const filtered = items.filter(
    (i) =>
      i.name?.toLowerCase().includes(search.toLowerCase()) ||
      i.name_en?.toLowerCase().includes(search.toLowerCase()) ||
      i.city?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Card className="p-5" data-testid="library-exhibition-panel">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Building2Icon className="w-5 h-5 mr-2 text-amber-600" />
            Fuar Merkezleri
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
            Platformda kullanılabilecek fuar merkezleri. Tüm müşteriler avans /
            seyahat taleplerinde bu listeden seçebilir.
          </p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="add-exhibition-btn"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Yeni Fuar Merkezi
        </Button>
      </div>

      <div className="relative mb-4">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Fuar merkezi, şehir veya ad ara..."
          className="pl-10"
          data-testid="library-exhibition-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="w-6 h-6 animate-spin text-amber-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
          {items.length === 0
            ? 'Henüz fuar merkezi eklenmemiş'
            : 'Sonuç bulunamadı'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((item) => (
            <div
              key={item.id}
              data-testid={`exhibition-row-${item.id}`}
              className="border border-gray-200 rounded-lg p-4 hover:border-amber-300 hover:bg-amber-50/30 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">
                    {item.name}
                  </h4>
                  {item.name_en && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      EN · {item.name_en}
                    </p>
                  )}
                  {(item.city || item.country_code) && (
                    <p className="text-xs text-gray-600 mt-1">
                      📍 {[item.city, item.country_code].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {item.address && (
                    <p className="text-xs text-gray-500 mt-1 truncate">
                      {item.address}
                    </p>
                  )}
                  {item.website && (
                    <a
                      href={item.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-indigo-600 hover:underline mt-1 inline-block truncate max-w-full"
                    >
                      {item.website}
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors"
                    data-testid={`edit-exhibition-${item.id}`}
                    aria-label="Düzenle"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    data-testid={`delete-exhibition-${item.id}`}
                    aria-label="Sil"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent
          className="max-w-lg max-h-[85vh] overflow-y-auto"
          data-testid="exhibition-modal"
        >
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Fuar Merkezini Düzenle' : 'Yeni Fuar Merkezi'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="ex-name">Ad (Türkçe)</Label>
              <Input
                id="ex-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Örn: CNR EXPO İstanbul"
                autoFocus
                data-testid="exhibition-name-input"
              />
            </div>
            <div>
              <Label htmlFor="ex-name-en">
                Name (English)
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="ex-name-en"
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                placeholder="e.g. CNR EXPO Istanbul"
                data-testid="exhibition-name-en-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ex-city">Şehir</Label>
                <Input
                  id="ex-city"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="Örn: İstanbul"
                  data-testid="exhibition-city-input"
                />
              </div>
              <div>
                <Label htmlFor="ex-country">Ülke Kodu</Label>
                <select
                  id="ex-country"
                  value={form.country_code}
                  onChange={(e) =>
                    setForm({ ...form, country_code: e.target.value })
                  }
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="exhibition-country-select"
                >
                  <option value="">— Seçin —</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} · {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <Label htmlFor="ex-address">
                Adres
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="ex-address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Tam adres"
                data-testid="exhibition-address-input"
              />
            </div>
            <div>
              <Label htmlFor="ex-website">
                Web Sitesi
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="ex-website"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://..."
                data-testid="exhibition-website-input"
              />
            </div>
            <div>
              <Label htmlFor="ex-notes">
                Notlar
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="ex-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Dahili notlar..."
                data-testid="exhibition-notes-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={saving}
            >
              İptal
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="exhibition-save-btn"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/* -------------------- Global Departments Panel (Ultra Admin CRUD) -------------------- */
const GlobalDepartmentsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({ name: '', name_en: '', is_active: true });
  const [saving, setSaving] = useState(false);

  const authHeader = () => {
    const t = localStorage.getItem('ultra_admin_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/ultra-admin/global-departments`, {
        headers: authHeader(),
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error('Departmanlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openAdd = () => {
    setEditingItem(null);
    setForm({ name: '', name_en: '', is_active: true });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name || '',
      name_en: item.name_en || '',
      is_active: item.is_active !== false,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    const name = (form.name || '').trim();
    if (!name) {
      toast.error('Departman adı (TR) zorunludur');
      return;
    }
    try {
      setSaving(true);
      const body = {
        name,
        name_en: form.name_en?.trim() || null,
        is_active: !!form.is_active,
      };
      if (editingItem) {
        await axios.put(
          `${API}/ultra-admin/global-departments/${editingItem.id}`,
          body,
          { headers: authHeader() },
        );
        toast.success('Departman güncellendi — tüm kullanıcılara yansır');
      } else {
        await axios.post(`${API}/ultra-admin/global-departments`, body, {
          headers: authHeader(),
        });
        toast.success('Departman eklendi — tüm kullanıcılara yansır');
      }
      setShowModal(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.name}" silinsin mi? Bu, tüm şirketlerin departman seçeneklerinden de kaldırılır.`)) return;
    try {
      await axios.delete(`${API}/ultra-admin/global-departments/${item.id}`, {
        headers: authHeader(),
      });
      toast.success('Departman silindi');
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silinemedi');
    }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(
        `${API}/ultra-admin/global-departments/${item.id}`,
        {
          name: item.name,
          name_en: item.name_en,
          is_active: !item.is_active,
        },
        { headers: authHeader() },
      );
      toast.success(!item.is_active ? 'Aktifleştirildi' : 'Pasifleştirildi');
      fetchItems();
    } catch (err) {
      toast.error('Durum değiştirilemedi');
    }
  };

  const filtered = items.filter(
    (i) =>
      i.name?.toLowerCase().includes(search.toLowerCase()) ||
      i.name_en?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <Card className="p-5" data-testid="library-departments-panel">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <UsersIcon className="w-5 h-5 mr-2 text-violet-600" />
            Global Departmanlar
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 max-w-xl">
            Bu departmanlar tüm şirketlerin kullanıcı yönetimi ekranlarında
            &quot;Varsayılan&quot; olarak görünür. Şirketler kendi özel departmanlarını
            ayrıca ekleyebilir; bunlar size yansımaz.
          </p>
        </div>
        <Button
          onClick={openAdd}
          className="bg-violet-600 hover:bg-violet-700 text-white"
          data-testid="add-department-btn"
        >
          <PlusIcon className="w-4 h-4 mr-1" />
          Yeni Departman
        </Button>
      </div>

      <div className="relative mb-4">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Departman ara (TR / EN)..."
          className="pl-10"
          data-testid="library-departments-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2Icon className="w-6 h-6 animate-spin text-violet-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
          {items.length === 0
            ? 'Henüz global departman tanımlanmamış'
            : 'Sonuç bulunamadı'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((item) => (
            <div
              key={item.id}
              data-testid={`department-row-${item.id}`}
              className={`border rounded-lg p-4 transition-colors ${
                item.is_active
                  ? 'border-gray-200 hover:border-violet-300 hover:bg-violet-50/30'
                  : 'border-gray-200 bg-gray-50 opacity-70'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">
                    {item.name}
                  </h4>
                  {item.name_en && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      EN · {item.name_en}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => toggleActive(item)}
                    className={`mt-2 inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${
                      item.is_active
                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    data-testid={`toggle-department-${item.id}`}
                  >
                    {item.is_active ? '● Aktif' : '○ Pasif'}
                  </button>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => openEdit(item)}
                    className="p-2 text-gray-500 hover:text-violet-600 hover:bg-violet-50 rounded-md transition-colors"
                    data-testid={`edit-department-${item.id}`}
                    aria-label="Düzenle"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    data-testid={`delete-department-${item.id}`}
                    aria-label="Sil"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent
          className="max-w-md"
          data-testid="department-modal"
        >
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Departmanı Düzenle' : 'Yeni Global Departman'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label htmlFor="dept-name">Departman Adı (Türkçe) *</Label>
              <Input
                id="dept-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Örn: Ar-Ge"
                autoFocus
                data-testid="department-name-input"
              />
            </div>
            <div>
              <Label htmlFor="dept-name-en">
                Department Name (English)
                <span className="text-xs text-gray-500 ml-1">— opsiyonel</span>
              </Label>
              <Input
                id="dept-name-en"
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                placeholder="e.g. R&D"
                data-testid="department-name-en-input"
              />
            </div>
            <label className="flex items-center gap-2 pt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) =>
                  setForm({ ...form, is_active: e.target.checked })
                }
                className="rounded"
              />
              <span className="text-sm text-gray-700">
                Aktif — kullanıcı seçim listesinde görünsün
              </span>
            </label>
          </div>
          <div className="flex items-center justify-end gap-2 pt-4 border-t mt-4">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              disabled={saving}
            >
              İptal
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="department-save-btn"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UltraLibrary;
